import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Avatar } from '@/components/atoms/Avatar';
import { PostCard, type Post } from '@/components/organisms/PostCard';
import { AdBanner } from '@/components/ads/AdBanner';
import { cn } from '@/lib/cn';
import { isoDate } from '@/lib/format';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import {
  formatTickerPrice,
  marketBadgeClass,
  changeColorClass,
  deriveMarket,
  type TickerBasicSummary,
  type TickerAssetType,
} from '@/lib/tickerMock';

/**
 * 검색 결과 — 탭 없이 섹션 나열 방식.
 * 서버 GET /search?q= 한 번 호출로 모든 섹션 동시 수신.
 * 각 섹션 5개까지 미리보기, 초과 시 "더보기 N건" 버튼으로 펼침.
 *
 * 매매일지는 posts 테이블이 아닌 trade_diaries 에 저장되므로
 * 서버 응답에서 posts.diary 가 아닌 별도 `diaries` 배열로 내려옴.
 */

const PREVIEW_COUNT = 5;

type BoardKey = 'coin' | 'stock' | 'notice' | 'global' | 'free';

interface TickerItem {
  ticker: string;
  asset_type: TickerAssetType;
  name: string;
  market?: string | null;
  price?: number | string | null;
  change_pct?: number | string | null;
  change_amount?: number | string | null;
  basic?: TickerBasicSummary;
}

interface UserItem {
  user_id: string;
  nickname: string;
  level: number;
  bio: string;
  profile_image_url: string | null;
}

interface DiaryItem {
  id: number;
  ticker: string;
  asset_type: 'stock' | 'coin' | 'us_stock';
  ticker_name: string | null;
  buy_date: string | null;
  sell_date: string | null;
  memo: string | null;
  is_certified: boolean;
  created_at: string;
  user_id: string;
  nickname: string;
}

interface SearchResults {
  tickers?: TickerItem[];
  posts?: Partial<Record<BoardKey, Post[]>>;
  diaries?: DiaryItem[];
  users?: UserItem[];
}

/* 섹션 순서: 종목 → 게시판 5종 → 매매일지 → 유저
   ※ 공시(notice) 는 2026-04-29 DART 공시 수집 재가동으로 다시 검색 결과 노출. */
const BOARD_SECTIONS: Array<{ key: BoardKey; label: string }> = [
  { key: 'coin',   label: '코인' },
  { key: 'stock',  label: '국내주식' },
  { key: 'notice', label: '공시 분석' },
  { key: 'global', label: '해외주식' },
  { key: 'free',   label: '자유게시판' },
];

export function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get('q') ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['search', q],
    queryFn: () => get<SearchResults>(`/search?q=${encodeURIComponent(q)}`),
    enabled: q.length > 0,
  });

  const tickers = data?.tickers ?? [];
  const users = data?.users ?? [];
  const posts = data?.posts ?? {};
  const diaries = data?.diaries ?? [];

  const totalCount =
    tickers.length +
    users.length +
    diaries.length +
    BOARD_SECTIONS.reduce((sum, b) => sum + (posts[b.key]?.length ?? 0), 0);

  return (
    <div className="container-base py-10">
      {q ? (
        <header className="mb-4">
          <p className="text-sm text-[var(--color-text-sub)]">
            <span className="font-semibold text-[var(--color-text)]">"{q}"</span> 검색 결과
          </p>
        </header>
      ) : (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-sm text-[var(--color-text-sub)]">
          상단 검색창에서 종목·유저·게시글을 검색해 주세요.
        </div>
      )}

      {isLoading && q && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {data && q && (
        <>
          {totalCount === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-sm text-[var(--color-text-sub)]">
              "{q}" 에 대한 검색 결과가 없어요.
            </div>
          ) : (
            <div className="divide-y divide-[var(--color-border)]">
              {tickers.length > 0 && (
                <Section title="종목" count={tickers.length}>
                  <Expandable total={tickers.length}>
                    {(shown) => (
                      <div className="space-y-2">
                        {tickers.slice(0, shown).map((t) => (
                          <TickerCard key={t.ticker} t={t} />
                        ))}
                      </div>
                    )}
                  </Expandable>
                </Section>
              )}

              {BOARD_SECTIONS.map((b) => {
                const list = posts[b.key] ?? [];
                if (list.length === 0) return null;
                return (
                  <Section key={b.key} title={b.label} count={list.length}>
                    <Expandable total={list.length}>
                      {(shown) => (
                        <div className="space-y-3">
                          {list.slice(0, shown).map((p) => (
                            <PostCard key={p.id} post={p} showBoard />
                          ))}
                        </div>
                      )}
                    </Expandable>
                  </Section>
                );
              })}

              {diaries.length > 0 && (
                <Section title="매매일지" count={diaries.length}>
                  <Expandable total={diaries.length}>
                    {(shown) => (
                      <div className="space-y-3">
                        {diaries.slice(0, shown).map((d) => (
                          <DiaryCard
                            key={d.id}
                            d={d}
                            onClick={() =>
                              navigate(`/diary/public/${encodeURIComponent(d.ticker)}`)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </Expandable>
                </Section>
              )}

              {users.length > 0 && (
                <Section title="유저" count={users.length}>
                  <Expandable total={users.length}>
                    {(shown) => (
                      <div className="space-y-2">
                        {users.slice(0, shown).map((u) => (
                          <UserCard key={u.user_id} u={u} />
                        ))}
                      </div>
                    )}
                  </Expandable>
                </Section>
              )}
            </div>
          )}

          {/* 광고: 검색 결과 하단 디스플레이 — multiplex 전역 비활성화로 display-rect 로 교체 (2026-05-29) */}
          {totalCount > 0 && (
            <AdBanner slot="display-rect" className="mt-4 sm:mt-8" />
          )}
        </>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="py-5 first:pt-0 last:pb-0">
      <div className="mb-3 flex items-center gap-1.5">
        <h2 className="text-base font-semibold text-[var(--color-text)]">{title}</h2>
        <span className="inline-flex items-center rounded-full bg-[var(--color-bg)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-sub)]">
          {count}
        </span>
      </div>
      {children}
    </section>
  );
}

/**
 * 5건까지 미리보기 후 "더보기 N건" 버튼으로 나머지를 펼침.
 * render prop 패턴 — 자식이 현재 `shown` 개수만큼 slice 해서 렌더.
 */
function Expandable({
  total,
  children,
}: {
  total: number;
  children: (shown: number) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? total : Math.min(total, PREVIEW_COUNT);
  const remainder = total - PREVIEW_COUNT;
  return (
    <>
      {children(shown)}
      {!expanded && remainder > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 w-full cursor-pointer rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] py-2 text-sm font-medium text-[var(--color-text-sub)] transition-colors hover:border-[var(--color-primary)]/40 hover:text-[var(--color-text)]"
        >
          더보기 {remainder}건
        </button>
      )}
    </>
  );
}

/** 전일대비 표기 — "+1,500원(+0.55%)" / "-16원(-5.18%)" / "0원(0.00%)" */
function formatTickerDelta(
  amount: number | null,
  pct: number,
  assetType: TickerAssetType,
): string {
  const isUsd = assetType === 'us_stock';
  const fmtAmt = (n: number) => {
    const sign = n > 0 ? '+' : '';
    if (isUsd) {
      return `${sign}$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `${sign}${n.toLocaleString('ko-KR')}원`;
  };
  const sPct = `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`;
  return amount != null ? `${fmtAmt(amount)}(${sPct})` : sPct;
}

function TickerCard({ t }: { t: TickerItem }) {
  /* 시세 수집 비활성화(2026-04-21 법인 전환) 상태 — price_cache 가 비어있는 게 현 정상.
     서버가 price/change_pct 를 줬을 때만 가격 영역을 노출하고, 없으면 종목명/시장 배지만 표시.
     (예전엔 mockBasic 으로 가짜 가격을 보여줬는데 사용자 혼란 — 그 폴백 제거) */
  const serverPrice = typeof t.price === 'string' ? Number(t.price) : t.price;
  const serverChangePct =
    typeof t.change_pct === 'string' ? Number(t.change_pct) : t.change_pct;
  const serverChangeAmount =
    typeof t.change_amount === 'string' ? Number(t.change_amount) : t.change_amount;
  const hasServerPrice =
    serverPrice != null && Number.isFinite(serverPrice) &&
    serverChangePct != null && Number.isFinite(serverChangePct);
  const changeAmount =
    serverChangeAmount != null && Number.isFinite(serverChangeAmount)
      ? (serverChangeAmount as number)
      : null;

  const basic: TickerBasicSummary | null = t.basic
    ? t.basic
    : hasServerPrice
      ? {
          price: serverPrice as number,
          changePct: serverChangePct as number,
          per: null,
          marketCap: '—',
        }
      : null;
  const market = t.market ?? deriveMarket(t.ticker, t.asset_type);
  const changeCls = basic ? changeColorClass(basic.changePct) : '';
  return (
    <Link
      to={`/ticker/${t.ticker}`}
      className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 transition-colors hover:border-[var(--color-primary)]/40"
    >
      <div className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate font-semibold text-[var(--color-text)]">
          {t.name || t.ticker}
        </span>
        <span className="shrink-0 rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-sub)]">
          {t.ticker}
        </span>
        <span
          className={cn(
            'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold',
            marketBadgeClass(market),
          )}
        >
          {market}
        </span>
      </div>
      {basic && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[var(--color-text-sub)]">
          <span className="inline-flex items-center gap-0.5">
            <span>현재가</span>
            <span className="font-medium text-[var(--color-text)] tabular-nums">
              {formatTickerPrice(basic, t.asset_type)}
            </span>
          </span>
          <span className="inline-flex items-center gap-0.5">
            <span>전일대비</span>
            <span className={cn('font-semibold tabular-nums', changeCls)}>
              {formatTickerDelta(changeAmount, basic.changePct, t.asset_type)}
            </span>
          </span>
          {basic.per !== null && (
            <span className="inline-flex items-center gap-0.5">
              <span>PER</span>
              <span className="font-medium text-[var(--color-text)] tabular-nums">
                {basic.per.toFixed(1)}
              </span>
            </span>
          )}
          {basic.marketCap !== '—' && (
            <span className="inline-flex items-center gap-0.5">
              <span>시총</span>
              <span className="font-medium text-[var(--color-text)] tabular-nums">
                {basic.marketCap}
              </span>
            </span>
          )}
        </div>
      )}
    </Link>
  );
}

function UserCard({ u }: { u: UserItem }) {
  return (
    <Link
      to={`/users/${u.user_id}`}
      className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
    >
      <Avatar src={u.profile_image_url ?? undefined} alt={u.nickname} size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate font-semibold">{u.nickname}</span>
          <LevelBadge level={u.level} size="sm" />
        </div>
        {u.bio && (
          <p className="text-xs text-[var(--color-text-sub)] line-clamp-1">{u.bio}</p>
        )}
      </div>
    </Link>
  );
}

/**
 * 매매일지 검색 결과 카드 — UserPublicDiaryPage 의 PublicDiaryCard 와 동일한 스타일.
 * 금융정보(가격/수량/손익) 노출 금지 규칙에 따라 종목명 + 매수/매도 태그 + 날짜 + 메모만 표시.
 */
function DiaryCard({ d, onClick }: { d: DiaryItem; onClick: () => void }) {
  const hasSell = !!d.sell_date;
  const hasBuy = !!d.buy_date;
  const tradeBadge = hasSell
    ? { label: '매도', cls: 'bg-[var(--color-rise)]/10 text-[var(--color-rise)] border-[var(--color-rise)]/20' }
    : { label: '매수', cls: 'bg-[var(--color-fall)]/10 text-[var(--color-fall)] border-[var(--color-fall)]/20' };
  const statusBadge = hasSell
    ? { label: '청산', cls: 'bg-[var(--color-rise)]/10 text-[var(--color-rise)] border-[var(--color-rise)]/20' }
    : { label: '보유 중', cls: 'bg-[var(--color-fall)]/10 text-[var(--color-fall)] border-[var(--color-fall)]/20' };

  const isCoin = d.asset_type === 'coin';
  const displayName =
    (isCoin ? COIN_NAMES_KR[d.ticker] : null) ??
    d.ticker_name ??
    (isCoin ? stripKrw(d.ticker) : d.ticker);

  return (
    <button
      onClick={onClick}
      className="w-full cursor-pointer rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition-colors hover:border-[var(--color-primary)]/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex min-w-[52px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusBadge.cls}`}
            >
              {statusBadge.label}
            </span>
            <span
              className={`inline-flex min-w-[44px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tradeBadge.cls}`}
            >
              {tradeBadge.label}
            </span>
            <span className="truncate font-bold">{displayName}</span>
            {/* 종목코드 chip — 모든 매매일지에 일관 노출 (이전엔 displayName=ticker 일 때 미노출되어 누락됐음) */}
            <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-sub)]">
              {d.ticker}
            </span>
          </div>

          {/* 작성자 닉네임 — 별도 줄 한 줄 차지, 잘리지 않게 truncate 만 적용 */}
          <div className="mt-1 truncate text-xs text-[var(--color-text-sub)]">
            {d.nickname}
          </div>

          <div className="mt-1 space-y-0.5 text-[11px] text-[var(--color-text-sub)]">
            {hasBuy && (
              <div>
                매수일 :{' '}
                <span className="text-[var(--color-text)]">{isoDate(d.buy_date as string)}</span>
              </div>
            )}
            {hasSell && (
              <div>
                매도일 :{' '}
                <span className="text-[var(--color-text)]">{isoDate(d.sell_date as string)}</span>
              </div>
            )}
            {!hasBuy && !hasSell && (
              <div>
                등록일 :{' '}
                <span className="text-[var(--color-text)]">{isoDate(d.created_at)}</span>
              </div>
            )}
          </div>

          {d.memo && (
            <p className="mt-2 line-clamp-2 text-sm text-[var(--color-text-sub)]">{d.memo}</p>
          )}
        </div>

        <ChevronRight className="h-5 w-5 flex-shrink-0 text-[var(--color-text-sub)]" />
      </div>
    </button>
  );
}
